<template>
	<div>
		<SelectionModal
			:title="$t('admin_people_add_to_groups_button')"
			:confirmText="confirmButtonText"
			:confirmProps="{ loading: saving }"
			:items="groups"
			:columns="columns"
			:selectedItemsById="selectedGroupsById"
			:loading="fetchingGroups"
			:loadingMore="loadingMoreGroups"
			v-if="!bulkErrors"
			class="add-to-groups"
			@selection="onSelectionChange"
			@load-more="loadMoreGroups"
			@confirm="addToGroups"
			@cancel="onClose"
			ref="modal"
		>
			<template #cell="{ data, selected }">
				<div class="cell-content">
					<div class="group-name">
						<div v-if="data.category" class="category">
							{{ data.category.title }}
						</div>
						<div class="group" v-t>{{ data.title }}</div>
					</div>
					<div class="already-selected" v-if="selected === SELECTION_FIXED">
						<LockIcon v-if="data.locked" />
						<span v-if="data.alreadyMember">{{
							$t('admin_groups_already_group_member_label')
						}}</span>
					</div>

					<div
						v-if="data.memberships_available === 0"
						class="group-full"
						v-t="'admin_groups_full_label'"
					/>
				</div>
			</template>

			<template #filters>
				<div class="filters">
					<Input
						:icon="SearchIcon"
						:placeholder="searchPlaceholder"
						class="search"
						v-model="query"
					/>
					<Dropdown
						search
						:options="categories"
						@search="searchCategories"
						v-model="selectedCategoryId"
						@load-more="loadMoreCategories"
						:loading="fetchingCategories"
						:loadingMore="loadingMoreCategories"
						placeholder="Category"
						class="category"
						ref="categoryDropdown"
					>
						<template v-if="!selectedCategoryId" #icon>
							<ShapesIcon type="regular" />
						</template>

						<template v-if="selectedCategoryId" #actionitem>
							<Button link class="reset-category" @click="resetCategory">{{
								`${$t('admin_people_reset_button')}`
							}}</Button>
						</template>

						<template v-if="selectedCategoryId" #selected="{ option }">
							{{ $t('admin_common_category_title') }}:
							<span class="selected-title">{{ option.title }}</span>
						</template>

						<template v-slot="{ option }">
							{{ option.title }}
						</template>
					</Dropdown>
				</div>
			</template>
		</SelectionModal>

		<BulkErrorModal
			v-if="bulkErrors"
			:successCount="bulkErrors.successCount"
			:errorCount="bulkErrors.errorCount"
			:errors="bulkErrors.errors"
			@confirm="onClose"
			@cancel="onClose"
		>
			<template #error-item-left="{ data }">
				<div
					class="error-category"
					v-if="data.category"
					v-text="data.category.title"
				/>
				<div class="error-group-title" v-text="data.group.title" />
			</template>
		</BulkErrorModal>
	</div>
</template>

<script>
import { Table, Input, Dropdown, Button } from '@/components/common'
import { get, post, SelectionModal, BulkErrorModal } from '../common'
import debounce from 'debounce'
import { ShapesIcon, SearchIcon, LockIcon } from '@/icons'
import { markRaw } from 'vue'

const PAGE_SIZE = 30

export default {
	components: {
		Input,
		Dropdown,
		Button,
		ShapesIcon,
		SelectionModal,
		LockIcon,
		BulkErrorModal,
	},

	props: { userId: [Number, String] },

	data() {
		return {
			categoryApiResponse: null,
			groupApiResponse: null,
			categories: [],
			columns: [{ field: 'title' }],
			groups: [],
			selectedGroupsById: {},
			selectedCategoryId: null,
			fetchingCategories: false,
			saving: false,
			fetchingGroups: true,
			loadingMoreCategories: false,
			loadingMoreGroups: false,
			categoryQuery: null,
			query: null,
			SELECTION_FIXED: Table.SELECTION_FIXED,
			SearchIcon: markRaw(SearchIcon),
			bulkErrors: null,
		}
	},

	emits: ['cancel', 'success'],

	created() {
		this.fetchCategoriesDebounced = debounce(
			this.fetchCategories.bind(this),
			500
		)
		this.fetchGroupsDebounced = debounce(this.fetchGroups.bind(this), 500)
		this.fixedSelectionsById = {}
	},

	mounted() {
		this.fetchCategories()
		this.fetchGroups()
	},

	computed: {
		confirmButtonText() {
			return this.selectedCount === 0
				? this.$t('admin_people_add_to_groups_button')
				: this.$t(
						'admin_people_add_to_multiple_groups_button.variable',
						{
							Count: this.selectedCount,
						},
						this.selectedCount
				  )
		},

		selectedCount() {
			return Object.values(this.selectedGroupsById).filter(
				(s) => s.selected === true
			).length
		},

		selectedCategory() {
			return this.selectedCategoryId
				? this.categories.find((c) => c.id === this.selectedCategoryId)
				: null
		},

		searchPlaceholder() {
			return this.selectedCategory
				? this.$t('admin_categories_search_groups_action', {
						CategoryName: this.selectedCategory.title,
				  })
				: this.$t('admin_people_search_action')
		},
	},

	watch: {
		query() {
			this.fetchingGroups = true
			this.fetchGroupsDebounced()
		},
		selectedCategoryId() {
			this.fetchingGroups = true
			this.fetchGroupsDebounced()
		},
	},

	methods: {
		onClose() {
			this.bulkErrors = null
			this.$emit('cancel')
		},

		onSelectionChange(selected = {}) {
			this.selectedGroupsById = Object.assign(
				{},
				selected,
				this.fixedSelectionsById
			)
		},

		setFixedSelections() {
			const { chats_with_user, data: groups } = this.groupApiResponse
			if (!chats_with_user?.length) return

			const systemRoles = groups
				.filter((g) => g.addmember === 'system')
				.reduce((acc, g) => {
					g.locked = true
					acc[g.id] = { item: g, selected: Table.SELECTION_FIXED }
					return acc
				}, {})

			this.fixedSelectionsById = chats_with_user
				.map((id) => groups.find((g) => g.id === id))
				.filter((g) => g)
				.reduce((acc, g) => {
					g.alreadyMember = true
					acc[g.id] = { item: g, selected: Table.SELECTION_FIXED }
					return acc
				}, this.fixedSelectionsById)

			this.selectedGroupsById = Object.assign(
				{},
				systemRoles,
				this.selectedGroupsById,
				this.fixedSelectionsById
			)
		},

		async addToGroups() {
			this.saving = true
			try {
				const group_ids = Object.values(this.selectedGroupsById)
					.filter((s) => s.selected === true)
					.map(({ item }) => item.id)
				await post(`/organization/users/${this.userId}/memberships/batch`, {
					group_ids,
				})
				this.$emit('success')
				this.onClose()
				this.$alert.success(
					this.$t(
						'admin_groups_add_people_success_label.variable',
						{
							Count: group_ids.length,
						},
						group_ids.length
					)
				)
			} catch (e) {
				console.error(e)
				this.bulkErrors = this.processBulkErrors(e)
			}
			this.saving = false
		},

		processBulkErrors(e) {
			// Since a single group might appear in multiple error code
			// groupings in the error reponse, we want to normalize those so
			// a given group only appears once in the list of errors but with
			// a message for each code
			const { data } = e.response
			if (!data || !data.errors) {
				this.$alert.error()
				return
			}

			// get flat list of all errors
			const allErrors = data.errors.group_ids
				.map((error) =>
					error.group_ids.map((group_id) => ({
						error,
						group_id,
					}))
				)
				.flat()
			// group all errors by group_id
			const errorsByGroupId = allErrors.reduce((acc, { error, group_id }) => {
				if (acc[group_id]) {
					acc[group_id].messages.push(error.message)
					return acc
				}
				const group = this.groups.find((g) => g.id === group_id)
				acc[group_id] = {
					group,
					category: this.categories.find((c) => c.id === group.category_id),
					messages: [error.message],
				}
				return acc
			}, {})
			const errorsToDisplay = Object.values(errorsByGroupId)
			return {
				successCount: data.success?.group_ids?.length ?? 0,
				errorCount: errorsToDisplay.length,
				errors: errorsToDisplay,
			}
		},

		async fetchCategories() {
			this.fetchingCategories = true
			const params = {
				sort: 'title',
				'page[size]': PAGE_SIZE,
			}

			if (this.categoryQuery) {
				params['filter[search]'] = this.categoryQuery
			}

			try {
				this.categoryApiResponse = await get('/organization/categories', params)
				this.categories = this.categoryApiResponse.data
				this.fetchingCategories = false
			} catch (e) {
				console.error(e)
				this.fetchingCategories = false
				this.$refs.modal?.$alert.error(this.$t('common_api_error_save'))
			}
		},

		async fetchGroups() {
			this.fetchingGroups = true
			const params = {
				include: 'category',
				sort: 'title',
				'page[size]': PAGE_SIZE,
			}
			if (this.query) {
				params['filter[search]'] = this.query
			}
			if (this.selectedCategoryId) {
				params['filter[category.id]'] = this.selectedCategoryId
			}
			if (this.userId) {
				params['additional[memberships.user_id]'] = this.userId
			}

			try {
				this.groupApiResponse = await get('/organization/groups', params)
				this.groups = this.groupApiResponse.data
				this.setFixedSelections()
				this.fetchingGroups = false
				this.$refs.modal.$refs.mainTable.scrollToTop()
			} catch (e) {
				console.error(e)
				this.fetchingGroups = false
				this.$refs.modal?.$alert.error(this.$t('common_api_error_save'))
			}
		},

		resetCategory() {
			this.selectedCategoryId = null
			this.categoryQuery = null
			this.$refs.categoryDropdown.close()
		},

		searchCategories(searchTerm) {
			this.categoryQuery = searchTerm
			this.fetchCategoriesDebounced()
		},

		async loadMoreCategories() {
			if (!this.categoryApiResponse?.next || this.loadingMoreCategories) return
			this.loadingMoreCategories = true
			try {
				this.categoryApiResponse = await this.categoryApiResponse.next()
				this.categories = this.categories.concat(this.categoryApiResponse.data)
				this.loadingMoreCategories = false
			} catch (e) {
				console.error(e)
				this.$refs.modal?.$alert.error(e)
			}
			this.loadingMoreCategories = false
		},

		async loadMoreGroups() {
			if (!this.groupApiResponse?.next || this.loadingMoreGroups) return
			this.loadingMoreGroups = true
			try {
				this.groupApiResponse = await this.groupApiResponse.next()
				this.groups = this.groups.concat(this.groupApiResponse.data)
				this.setFixedSelections()
				this.loadingMoreGroups = false
			} catch (e) {
				console.error(e)
				this.$refs.modal?.$alert.error(e)
			}
			this.loadingMoreGroups = false
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.selected-title {
	font-family: Avenir-Book;
	font-size: 16px;
	color: var(--blue-300);
}

.group-full {
	margin-right: 10px;
	font-family: Avenir-Book;
	font-style: italic;
	font-size: 16px;
	color: var(--neutral-600);
}

.filters {
	@include flex;
	width: 100%;
	.search {
		margin-right: 20px;
		flex: 2;
	}

	.category {
		flex: 1;
		.pronto-icon {
			margin-right: 5px;
			font-size: 18px;
		}

		:deep(.open) {
			max-height: 290px;
		}

		:deep(.dropdown-selected) {
			background: #fafafa;
			.dropdown-selected-label {
				@include clamp(1);
			}
			font-family: Avenir-Book;
			font-size: 16px;
			color: var(--neutral-600);
		}
	}
}

:deep(.table-cell-content) {
	width: 100%;
}
.cell-content {
	@include flex-spread;
	width: 100%;
	.group-name {
		font-family: Avenir;
		font-style: normal;
		color: var(--neutral-800);

		.category {
			font-weight: normal;
			font-size: 12px;
			line-height: 16px;
			text-transform: uppercase;
		}

		.group {
			font-weight: 800;
			font-size: 15px;
			line-height: 20px;
		}
	}
}

.error-category {
	text-transform: uppercase;
	font-family: Avenir-Book;
	font-size: 12px;
	@include ellipsis;
}
.error-group {
	font-family: Avenir-Heavy;
	font-size: 15px;
	@include ellipsis;
}
</style>
